import { useRef } from "react";
import {
  ChevronLeft, ChevronRight, ArrowRight, Globe, BadgePercent, PiggyBank, Instagram,
} from "lucide-react";
import { Reveal } from "../smart/Reveal";
import { BENEFIT_INDICATORS, NATIONAL_BENEFITS, GLOBAL_BENEFITS, LINKS } from "../../data/arccaContent";
import { NationalBenefitsCarousel } from "../shared/NationalBenefitsCarousel";

const IND_ICONS = [Globe, BadgePercent, PiggyBank];

const Eyebrow = ({ children }) => (
  <p className="text-xs font-display font-bold uppercase tracking-[0.2em] text-arcca-dark">{children}</p>
);

const NavBtn = ({ dir, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-label={dir === "left" ? "Înapoi" : "Înainte"}
    className={`hidden md:flex absolute top-1/2 -translate-y-1/2 z-10 h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-arccaink shadow-sm hover:bg-slate-50 transition-colors ${
      dir === "left" ? "-left-3" : "-right-3"
    }`}
  >
    {dir === "left" ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
  </button>
);

const Carousel = ({ testId, children }) => {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.8, behavior: "smooth" });
  return (
    <div className="relative" data-testid={testId}>
      <NavBtn dir="left" onClick={() => scroll(-1)} testId={`${testId}-prev`} />
      <div ref={ref} className="flex gap-5 overflow-x-auto scroll-smooth snap-x pb-2 no-scrollbar">
        {children}
      </div>
      <NavBtn dir="right" onClick={() => scroll(1)} testId={`${testId}-next`} />
    </div>
  );
};

/* ---------- Beneficiile ISIC ---------- */
export const ArccaBenefits = () => (
  <section id="beneficii" data-testid="arcca-benefits" className="scroll-mt-16 bg-slate-50 py-16 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="max-w-3xl">
        <Reveal><Eyebrow>Beneficiile tale de student</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-arccaink text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
            Statutul de student vine cu avantaje. Profită de ele.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
            Cu ISIC, ai acces la reduceri și beneficii în România și în peste 130 de țări. De la cafea, shopping
            și restaurante, până la travel, cultură și servicii, BRD-ISIC îți aduce mai multe motive să îl
            folosești și în afara campusului.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {BENEFIT_INDICATORS.map((ind, i) => {
            const Icon = IND_ICONS[i];
            return (
              <div key={ind.label} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-6 py-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-arcca/15 text-arcca-dark">
                  <Icon size={20} strokeWidth={2.1} />
                </span>
                <div>
                  <p className="font-display text-2xl font-extrabold text-arccaink leading-none">{ind.value}</p>
                  <p className="mt-1 text-sm text-slate-500">{ind.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Reveal>

      {/* National carousel */}
      <div className="mt-14">
        <h3 className="font-display font-bold text-lg text-arccaink">Beneficii în România</h3>
        <div className="mt-5">
          <NationalBenefitsCarousel
            items={NATIONAL_BENEFITS}
            allLink="https://www.isic.ro/reduceri"
            testIdPrefix="benefits-national"
            cardHoverClass="hover:border-arcca/40"
            textClass="text-arccaink"
            fallbackClass="font-display font-extrabold text-lg text-arccaink text-center"
            buttonClass="font-display text-arccaink hover:border-arcca hover:text-arcca-dark"
          />
        </div>
      </div>

      {/* Instagram — mențiune discretă */}
      <Reveal delay={0.1}>
        <div
          data-testid="instagram-card"
          className="mt-8 overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-arcca/10 via-white to-arccablue/10"
        >
          <div className="flex flex-col sm:flex-row items-center gap-6 p-6 sm:p-8">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white shadow-lg">
              <Instagram size={26} strokeWidth={2} />
            </span>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-display font-extrabold text-arccaink text-xl sm:text-2xl leading-snug">
                Nu rata ce apare doar pe Insta.
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600 max-w-xl">
                Pe @legitimatia.isic postăm reduceri, oferte limitate, concursuri, idei utile pentru studenți și
                noutăți despre beneficiile ISIC.
              </p>
            </div>
            <a
              href="https://www.instagram.com/legitimatia.isic/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-link"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-arccaink px-6 py-3.5 text-sm font-display font-bold text-white hover:bg-black transition-colors whitespace-nowrap"
            >
              <Instagram size={16} strokeWidth={2.2} /> Urmărește @legitimatia.isic
            </a>
          </div>
        </div>
      </Reveal>

      {/* International carousel */}
      <div className="mt-12">
        <h3 className="font-display font-bold text-lg text-arccaink">Beneficii internaționale</h3>
        <div className="mt-5">
          <Carousel testId="benefits-global">
            {GLOBAL_BENEFITS.map((d, i) => (
              <div
                key={d.image}
                data-testid={`benefit-global-${i}`}
                className="snap-start shrink-0 w-64 overflow-hidden rounded-3xl border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
              >
                <img src={d.image} alt={d.alt} className="w-full h-auto" loading="lazy" />
              </div>
            ))}
          </Carousel>
        </div>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-12 flex justify-center">
          <a
            href={LINKS.discountsIntl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="benefits-cta"
            className="inline-flex items-center gap-2 rounded-full bg-arccaink px-7 py-3.5 text-sm font-display font-bold text-white hover:bg-black transition-colors"
          >
            Explorează beneficiile ISIC <ArrowRight size={16} />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);
